import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { idSuffix } from "@/lib/idLabel";
import { formatDateTime } from "@/lib/dates";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, AlertCircle, RefreshCw, Clock, Loader2, Package, Factory, Lock, Save, Upload, X as XIcon, FileCheck } from "lucide-react";

/**
 * Map a shipment doc to a richer review state than the raw `admin_status` so
 * the admin can distinguish:
 *   - awaiting_review: brand-new pending shipment, never reviewed
 *   - resubmitted:     facility re-edited a previously rejected shipment and it
 *                      is back in the admin queue
 *   - rejected:        admin rejected; ball is in the facility's court until
 *                      they resubmit (no admin action available)
 *   - approved:        terminal happy path
 */
export function getShipmentReviewState(s) {
  if (!s) return { key: "awaiting_review", label: "Awaiting review" };
  const status = (s.admin_status || "pending").toLowerCase();
  if (status === "approved") {
    return {
      key: "approved",
      label: "Approved",
      tone: "ok",
      pillClass: "bg-[#D2E4D6] text-[#056839]",
      Icon: CheckCircle2,
    };
  }
  if (status === "rejected") {
    return {
      key: "rejected",
      label: "Awaiting facility resubmission",
      shortLabel: "Awaiting facility",
      tone: "warn",
      pillClass: "bg-[#E4D5B7] text-[#A0522D]",
      Icon: AlertCircle,
    };
  }
  // status === "pending" — distinguish first-time vs resubmitted
  if (s.resubmitted_at) {
    return {
      key: "resubmitted",
      label: "Resubmitted — review again",
      shortLabel: "Resubmitted",
      tone: "info",
      pillClass: "bg-[#DBE7F1] text-[#1E4E79]",
      Icon: RefreshCw,
    };
  }
  return {
    key: "awaiting_review",
    label: "Awaiting your review",
    shortLabel: "Awaiting review",
    tone: "neutral",
    pillClass: "bg-[#3F3F3F] text-white",
    Icon: Clock,
  };
}

function RecordId({ id }) {
  if (!id) return null;
  return (
    <span
      className="text-[10px] font-mono text-[#3F3F3F]/40 select-all"
      title={id}
      data-testid={`record-id-${id}`}
    >
      ID: {String(id).slice(0, 8)}…
    </span>
  );
}

export function HistoryDetailDialog({ detail, onClose }) {
  const { user } = useAuth();
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);
  // Nested provenance dialog — when the user clicks a Processing/Collection
  // chip inside a shipment detail, we open a second HistoryDetailDialog on
  // top with the traced record.
  const [nested, setNested] = useState(null);

  async function submitReview(action) {
    setReviewLoading(true);
    try {
      await api.post(`/admin/shipments/${detail.id}/review`, {
        action,
        rejection_reason: action === "rejected" ? rejectReason : ""
      });
      toast.success(action === "approved" ? "Shipment approved" : "Shipment rejected — facility notified");
      setShowRejectInput(false);
      setRejectReason("");
      onClose();
    } catch {
      toast.error("Review action failed");
    } finally {
      setReviewLoading(false);
    }
  }

  const open = !!detail;
  const kind = detail?.kind;
  const d = detail?.data;
  const fileUrl = (p) =>
    p ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${encodeURIComponent(p)}` : null;
  const fmt = (s) => {
    if (!s) return "—";
    try { return formatDateTime(s); } catch { return s; }
  };

  // Resolve the underlying shipment doc (kind === "shipped" wraps it inside d.shipment)
  const shipmentDoc = kind === "shipped" ? d?.shipment : null;
  const reviewState = shipmentDoc ? getShipmentReviewState(shipmentDoc) : null;
  const isShipmentReview = user?.role === "admin" && detail?.type === "shipment";

  return (
    <Dialog open={open} onOpenChange={(o) => {
      if (!o) {
        setShowRejectInput(false);
        setRejectReason("");
        onClose();
      }
    }}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto p-0"
        data-testid="history-detail-dialog"
      >
        {/* Status banner — replaces the floating "Admin: pending" pill. Gives
            admins an at-a-glance read of the review state, including the
            rejection reason or resubmission timestamp when relevant. */}
        {reviewState && (
          <div
            className={`px-6 py-3 border-b border-[#D2E4D6] flex items-start gap-3 ${
              reviewState.tone === "ok"     ? "bg-[#F0FAF4]" :
              reviewState.tone === "warn"   ? "bg-[#FBF3EA]" :
              reviewState.tone === "info"   ? "bg-[#EEF4FA]" :
                                              "bg-[#F9F8F6]"
            }`}
            data-testid={`shipment-status-banner-${reviewState.key}`}
          >
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold uppercase tracking-wider shrink-0 ${reviewState.pillClass}`}
            >
              <reviewState.Icon className="w-3.5 h-3.5" />
              {reviewState.shortLabel || reviewState.label}
            </span>
            <div className="text-xs text-[#3F3F3F] leading-relaxed flex-1">
              {reviewState.key === "awaiting_review" && (
                <>This shipment is in your queue. Review the evidence below, then approve or reject.</>
              )}
              {reviewState.key === "resubmitted" && (
                <>
                  Facility resubmitted on <strong>{fmt(shipmentDoc.resubmitted_at)}</strong>.
                  {shipmentDoc.reviewed_at && (
                    <> Originally reviewed on {fmt(shipmentDoc.reviewed_at)}.</>
                  )}
                </>
              )}
              {reviewState.key === "rejected" && (
                <>
                  Rejected on <strong>{fmt(shipmentDoc.reviewed_at)}</strong>
                  {shipmentDoc.reviewed_by_name ? ` by ${shipmentDoc.reviewed_by_name}` : ""}.
                  Waiting for facility to fix and resubmit.
                  {shipmentDoc.rejection_reason && (
                    <div className="mt-1 text-[#A0522D]">
                      Reason: <em>{shipmentDoc.rejection_reason}</em>
                    </div>
                  )}
                </>
              )}
              {reviewState.key === "approved" && (
                <>
                  Approved on <strong>{fmt(shipmentDoc.reviewed_at)}</strong>
                  {shipmentDoc.reviewed_by_name ? ` by ${shipmentDoc.reviewed_by_name}` : ""}.
                </>
              )}
            </div>
          </div>
        )}

        <div className="px-6 py-5">
        {kind === "received" && d && (
          <ReceivedDetail c={d} fileUrl={fileUrl} fmt={fmt} />
        )}
        {kind === "processed" && d && (
          <ProcessedDetail
            p={d}
            fmt={fmt}
            onOpenNested={setNested}
            currentUser={user}
          />
        )}
        {kind === "shipped" && d && (
          <ShippedDetail
            shipment={d.shipment}
            destination={d.destination}
            fileUrl={fileUrl}
            fmt={fmt}
            onOpenNested={setNested}
            currentUser={user}
            onClose={onClose}
          />
        )}
        </div>

        {/* Admin review action bar — sticky to the bottom of the dialog so the
            primary action is always visible without scrolling, and only
            actionable when admin actually has a decision to make. */}
        {isShipmentReview && reviewState && (reviewState.key === "awaiting_review" || reviewState.key === "resubmitted") && (
          <div
            className="sticky bottom-0 bg-white border-t border-[#D2E4D6] px-6 py-4 space-y-3"
            data-testid="admin-review-block"
          >
            {showRejectInput && (
              <div className="space-y-1">
                <label className="text-[10px] uppercase tracking-wider text-[#A0522D] font-medium">
                  Rejection reason (shown to facility)
                </label>
                <Textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Explain what's wrong so the facility can fix it…"
                  rows={3}
                  className="resize-none"
                  data-testid="admin-reject-reason"
                />
              </div>
            )}
            <div className="flex gap-2">
              {!showRejectInput ? (
                <>
                  <Button
                    onClick={() => submitReview("approved")}
                    disabled={reviewLoading}
                    className="flex-1 bg-[#056839] hover:bg-[#044E2A] text-white h-10"
                    data-testid="admin-approve-btn"
                  >
                    {reviewLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-1.5" /> Approve shipment</>}
                  </Button>
                  <Button
                    onClick={() => setShowRejectInput(true)}
                    disabled={reviewLoading}
                    variant="outline"
                    className="flex-1 border-[#A0522D] text-[#A0522D] hover:bg-[#FBF3EA] h-10"
                    data-testid="admin-reject-btn"
                  >
                    <AlertCircle className="w-4 h-4 mr-1.5" /> Reject
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    onClick={() => { setShowRejectInput(false); setRejectReason(""); }}
                    disabled={reviewLoading}
                    variant="outline"
                    className="h-10"
                    data-testid="admin-reject-cancel"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => submitReview("rejected")}
                    disabled={reviewLoading || !rejectReason.trim()}
                    className="flex-1 bg-[#A0522D] hover:bg-[#8B4423] text-white h-10"
                    data-testid="admin-reject-confirm"
                  >
                    {reviewLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm reject & notify facility"}
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </DialogContent>
      {nested && (
        <HistoryDetailDialog
          detail={nested}
          onClose={() => setNested(null)}
        />
      )}
    </Dialog>
  );
}

function Field({ label, children, testId }) {
  return (
    <div data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">{label}</div>
      <div className="mt-0.5 text-sm text-[#000000]">{children}</div>
    </div>
  );
}

function MaterialsBreakdown({ c }) {
  // Prefer per-material array; fall back to a single legacy row so old
  // records still render a breakdown card instead of vanishing.
  const rows =
    (c.materials || []).length > 0
      ? (c.materials || []).map((m) => ({
          material_type: m.material_type || m.material || "—",
          weight_kg: Number(m.weight_kg || 0),
        }))
      : c.material_type
      ? [{
          material_type: c.material_type,
          weight_kg: Number(c.received_weight_kg || c.estimated_weight_kg || c.total_weight_kg || 0),
        }]
      : [];
  if (rows.length === 0) return null;
  const total = rows.reduce((s, r) => s + (r.weight_kg || 0), 0);
  const isReceived = c.status === "Received";
  const label = isReceived ? "Received per material" : "Collector-declared per material";
  return (
    <div
      className="mt-5 rounded-xl border border-[#D2E4D6]/80 p-4 bg-[#F9F8F6]"
      data-testid="detail-materials-breakdown"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-[0.18em] text-[#69A488] font-medium">
          {label}
        </div>
        <div className="text-[11px] text-[#3F3F3F]/60">
          {rows.length} material{rows.length === 1 ? "" : "s"} · Total{" "}
          <span className="font-mono font-semibold text-[#056839]">{total.toFixed(1)} kg</span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-[#3F3F3F]/60 border-b border-[#D2E4D6]">
              <th className="py-1.5 pr-3">Material</th>
              <th className="py-1.5 pr-3 text-right">Weight (kg)</th>
              <th className="py-1.5 text-right">Share</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const share = total > 0 ? (r.weight_kg / total) * 100 : 0;
              return (
                <tr
                  key={`${r.material_type}-${i}`}
                  className="border-b border-[#D2E4D6]/60 last:border-b-0"
                  data-testid={`materials-row-${r.material_type}`}
                >
                  <td className="py-2 pr-3">
                    <span className="px-2 py-0.5 rounded-md bg-[#D2E4D6] text-[#056839] text-[10px] font-semibold uppercase tracking-wider">
                      {r.material_type}
                    </span>
                  </td>
                  <td className="py-2 pr-3 text-right font-mono text-[#056839] font-semibold">
                    {r.weight_kg.toFixed(1)}
                  </td>
                  <td className="py-2 text-right text-[#3F3F3F]/70 text-xs">
                    {share.toFixed(0)}%
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function ReceivedDetail({ c, fileUrl, fmt }) {
  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-heading text-2xl">
          Collection · {c.source_location}
        </DialogTitle>
        <p className="text-xs text-[#3F3F3F]/70">Material flow record</p>
        <RecordId id={c.id} />
      </DialogHeader>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
        <Field label="Status">
          <span
            className={`px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wider ${
              c.status === "Received"
                ? "bg-[#D2E4D6] text-[#056839]"
                : c.status === "Rejected"
                ? "bg-[#A0522D]/15 text-[#A0522D]"
                : "bg-[#E4D5B7] text-[#A0522D]"
            }`}
          >
            {c.status}
          </span>
        </Field>
        <Field label="Material">
          {(() => {
            const mats = c.materials || [];
            const labels = mats.length > 0
              ? Array.from(new Set(mats.map((m) => m.material_type || m.material).filter(Boolean)))
              : (c.material_type ? [c.material_type] : []);
            if (labels.length === 0) return "—";
            return (
              <div className="flex flex-wrap gap-1">
                {labels.map((l) => (
                  <span
                    key={l}
                    className="px-2 py-0.5 rounded-md bg-[#D2E4D6] text-[#056839] text-[10px] font-semibold uppercase tracking-wider"
                  >
                    {l}
                  </span>
                ))}
              </div>
            );
          })()}
        </Field>
        <Field label="Collector">{c.collector_name ? `${c.collector_name}${idSuffix(c.collector_id)}` : "—"}</Field>
        <Field label="Facility">{c.destination_facility_name ? `${c.destination_facility_name}${idSuffix(c.destination_facility_id)}` : "—"}</Field>
        <Field label="Address">{c.address || "—"}</Field>
        <Field label="Reference number">{c.reference_number || "—"}</Field>
        <Field label="Pickup time">{fmt(c.pickup_at)}</Field>
        <Field label="Submitted">{fmt(c.timestamp)}</Field>
        <Field label="Received">{fmt(c.date_received)}</Field>
        <Field label="Collector-declared weight">
          {(() => {
            const mats = c.materials || [];
            let kg = mats.length > 0
              ? mats.reduce((s, m) => s + (Number(m.weight_kg) || 0), 0)
              : 0;
            if (!kg) kg = Number(c.total_weight_kg) || Number(c.estimated_weight_kg) || 0;
            return kg ? `${kg} kg` : "—";
          })()}
        </Field>
        <Field label="Received weight">
          <span className="text-[#056839] font-semibold">
            {c.received_weight_kg ?? "—"} kg
          </span>
        </Field>
        {c.rejection_reason && (
          <Field label="Rejection reason">
            <span className="text-[#A0522D]">{c.rejection_reason}</span>
          </Field>
        )}
        {c.collector_email && (
          <Field label="Submitted by" testId="detail-submitted-by">
            {c.collector_email}
          </Field>
        )}
        {c.actioned_by_email && (
          <Field label={c.status === "Rejected" ? "Rejected by" : "Received by"} testId="detail-actioned-by">
            {c.actioned_by_name
              ? <>{c.actioned_by_name} <span className="text-[#3F3F3F]/60">({c.actioned_by_email})</span></>
              : c.actioned_by_email}
          </Field>
        )}
        {c.actioned_at && (
          <Field label="Action date" testId="detail-actioned-at">{fmt(c.actioned_at)}</Field>
        )}
      </div>

      {/* Per-material breakdown — one line per material with its kg. Shows the
          received breakdown when the collection has been reviewed (`materials[]`
          is replaced with facility-confirmed weights on receipt) and the
          estimated one otherwise. Falls back to the legacy single-material
          field if the array is empty. */}
      <MaterialsBreakdown c={c} />

      {/* Transaction block */}
      <div className="mt-5 rounded-xl border border-[#D2E4D6]/80 p-4 bg-[#F9F8F6]" data-testid="detail-transaction">
        <div className="text-xs uppercase tracking-[0.18em] text-[#69A488] font-medium mb-2">
          Transaction
        </div>
        {c.transaction_amount != null ? (
          <div className="flex flex-wrap items-center gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#3F3F3F]/60">Amount paid</div>
              <div className="text-2xl font-heading font-semibold text-[#000000]">
                {Number(c.transaction_amount).toFixed(2)}{" "}
                <span className="text-base text-[#3F3F3F]/70">{c.transaction_currency || ""}</span>
              </div>
            </div>
            <div className="ml-auto">
              {c.payment_proof_path ? (
                <a
                  href={fileUrl(c.payment_proof_path)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="detail-payment-proof-link"
                  className="inline-flex items-center gap-2 px-4 h-10 rounded-lg bg-[#056839] text-white text-sm font-medium hover:bg-[#044E2A]"
                >
                  Open bank transfer proof
                </a>
              ) : (
                <span className="text-xs text-[#A0522D]">No proof on file</span>
              )}
            </div>
          </div>
        ) : (
          <div className="text-sm text-[#3F3F3F]/60">No transaction recorded for this collection.</div>
        )}
      </div>

      {/* Photos - supports photos[] array and legacy photo_path */}
      {((c.photos && c.photos.length > 0) || c.photo_path) && (
        <div className="mt-5" data-testid="detail-photo-block">
          <div className="text-xs uppercase tracking-[-0.18em] text-[#69A488] font-medium mb-2">
            Collection photos
          </div>
          <div className="flex flex-wrap gap-3">
            {(c.photos && c.photos.length > 0
              ? c.photos
              : [{ path: c.photo_path }]
            ).map((p, i) => {
              const src = fileUrl(p.path || p.storage_path || p);
              return (
                <div key={(p && (p.path || p.storage_path)) || `pickup-photo-${i}`} className="flex flex-col items-center">
                  <a href={src} target="_blank" rel="noreferrer">
                    <img
                      src={src}
                      alt={`Collection ${i + 1}`}
                      className="w-40 h-40 object-cover rounded-lg border border-[#D2E4D6]/80 bg-white"
                    />
                  </a>
                  <a href={src} download
                    className="mt-1 w-full inline-flex items-center justify-center gap-1 px-2 h-7 rounded border border-[#056839] text-[#056839] text-xs font-medium hover:bg-[#F0FAF4]">
                    ↓ Download
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}

function ProcessedDetail({ p, fmt, onOpenNested, currentUser }) {
  const [loadingChip, setLoadingChip] = useState(null); // id while fetching
  const collectionIds = p.input_collection_ids || [];

  // Lazy-load a collection via /collections and open it as a nested dialog —
  // mirrors the shipment provenance chip behaviour so processing batches now
  // trace back to their originating collections the same way shipments do.
  async function openCollectionChip(id) {
    setLoadingChip(id);
    try {
      const { data } = await api.get("/collections");
      const doc = Array.isArray(data) ? data.find((x) => x.id === id) : null;
      if (!doc) {
        toast.error("Collection not found or you don't have access to it");
        return;
      }
      onOpenNested?.({ kind: "received", data: doc });
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Failed to load collection");
    } finally {
      setLoadingChip(null);
    }
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-heading text-2xl">
          Processing run · {fmt(p.processing_date)}
        </DialogTitle>
        <p className="text-xs text-[#3F3F3F]/70">
          Processing run{p.facility_name ? ` · ${p.facility_name}${idSuffix(p.facility_id)}` : ""}
        </p>
        <RecordId id={p.id} />
      </DialogHeader>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
        <Field label="Processing types">
          <div className="flex flex-wrap gap-1 mt-1">
            {(p.processing_types || []).map((t) => (
              <span
                key={t}
                className="px-2 py-0.5 rounded-md bg-[#E4D5B7]/60 text-[#A0522D] text-[10px] font-semibold uppercase tracking-wider"
              >
                {t}
              </span>
            ))}
          </div>
        </Field>
        <Field label="Input collections">{collectionIds.length} batches</Field>
        <Field label="Total input weight">{Number(p.total_input_weight_kg || 0).toFixed(1)} kg</Field>
        <Field label="Residual weight">{Number(p.residual_weight_kg || 0).toFixed(1)} kg</Field>
      </div>

      {/* Provenance — trace back to originating collections. Mirrors the
          shipment provenance block so processing batches are navigable the
          same way shipments are. */}
      {onOpenNested && collectionIds.length > 0 && (
        <div className="mt-5" data-testid="detail-processing-provenance">
          <div className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">Provenance</div>
          <div className="mt-2">
            <div className="text-[10px] uppercase tracking-wider text-[#3F3F3F]/50 mb-1">
              Collection{collectionIds.length === 1 ? "" : "s"} ({collectionIds.length})
            </div>
            <div className="flex flex-wrap gap-2">
              {collectionIds.map((cid) => (
                <button
                  key={cid}
                  type="button"
                  onClick={() => openCollectionChip(cid)}
                  disabled={!!loadingChip}
                  className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full border border-[#056839] text-[#056839] bg-white text-xs font-mono hover:bg-[#056839] hover:text-white transition-colors disabled:opacity-50"
                  data-testid={`processing-provenance-collection-${cid}`}
                >
                  {loadingChip === cid ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Package className="w-3 h-3" />
                  )}
                  {String(cid).slice(0, 8)}…
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="mt-5" data-testid="detail-outputs-block">
        <div className="text-xs uppercase tracking-[0.18em] text-[#69A488] font-medium mb-2">
          Outputs ({(p.outputs || []).length})
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#D2E4D6]/40">
              <tr className="text-left text-[10px] uppercase tracking-wider text-[#3F3F3F]">
                <th className="px-3 py-2">Material</th>
                <th className="px-3 py-2">Form</th>
                <th className="px-3 py-2 text-right">Weight (kg)</th>
                <th className="px-3 py-2 text-right">Status</th>
              </tr>
            </thead>
            <tbody>
              {(p.outputs || []).map((o) => (
                <tr key={o.id} className="border-t border-[#D2E4D6]/60">
                  <td className="px-3 py-2">
                    <span className="px-2 py-0.5 rounded-md bg-[#D2E4D6] text-[#056839] text-[10px] font-semibold uppercase tracking-wider">
                      {o.material_type}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-[#3F3F3F]">{o.form || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono text-[#056839] font-semibold">
                    {Number(o.weight_kg).toFixed(1)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <span
                      className={`text-[10px] uppercase tracking-wider font-semibold ${
                        o.shipped ? "text-[#3F3F3F]/60" : "text-[#056839]"
                      }`}
                    >
                      {o.shipped ? "Shipped" : "In stock"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function ShippedDetail({ shipment, destination, fileUrl, fmt, onOpenNested, currentUser, onClose }) {
  const d = destination || {};
  const [prov, setProv] = useState(null);
  const [provErr, setProvErr] = useState(null);
  const [loadingChip, setLoadingChip] = useState(null); // {kind, id} while fetching
  const isApproved = (shipment?.admin_status || "").toLowerCase() === "approved";
  const role = currentUser?.role;
  const canEditPayment = isApproved && (role === "facility" || role === "admin");
  const canEditNotes = isApproved && role === "admin";
  // Local view of the destination so post-approval edits reflect immediately.
  const [liveDest, setLiveDest] = useState(d);
  useEffect(() => { setLiveDest(d); }, [d]);

  useEffect(() => {
    let cancelled = false;
    if (!shipment?.id) {
      setProv(null);
      return () => { cancelled = true; };
    }
    setProv(null);
    setProvErr(null);
    (async () => {
      try {
        const { data } = await api.get(`/shipments/${shipment.id}/provenance`);
        if (!cancelled) setProv(data);
      } catch (e) {
        if (!cancelled) setProvErr(e?.response?.data?.detail || "Failed to load provenance");
      }
    })();
    return () => { cancelled = true; };
  }, [shipment?.id]);

  async function openChip(kind, id) {
    // Lazy-load the underlying record via the existing list endpoints and open
    // the appropriate nested detail dialog. We deliberately avoid adding new
    // single-record GET endpoints to keep scope tight.
    setLoadingChip({ kind, id });
    try {
      const path = kind === "processed" ? "/processing" : "/collections";
      const { data } = await api.get(path);
      const doc = Array.isArray(data) ? data.find((x) => x.id === id) : null;
      if (!doc) {
        toast.error("Record not found or you don't have access to it");
        return;
      }
      onOpenNested?.({ kind, data: doc });
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Failed to load record");
    } finally {
      setLoadingChip(null);
    }
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-heading text-2xl">
          Shipment · {d.destination_name}
        </DialogTitle>
        <p className="text-xs text-[#3F3F3F]/70">
          {d.destination_type} · {fmt(d.transaction_at)}
          {shipment?.facility_name ? ` · ${shipment.facility_name}${idSuffix(shipment.facility_id)}` : ""}
        </p>
        {shipment?.shipment_source === "surplus_sale" && (
          <span
            className="inline-flex w-fit items-center gap-1 mt-1 px-2 py-0.5 rounded-md bg-[#A0522D]/15 text-[#A0522D] text-[10px] font-semibold uppercase tracking-wider"
            data-testid="surplus-sale-badge"
            title="No linked processing batch — surplus / invoice-only sale"
          >
            <AlertCircle className="w-3 h-3" /> Surplus sale
          </span>
        )}
        <RecordId id={shipment?.id} />
      </DialogHeader>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
        {isApproved && (
          <div className="md:col-span-2 -mb-1 text-[11px] text-[#3F3F3F]/60 flex items-center gap-1.5" data-testid="approved-locked-hint">
            <Lock className="w-3 h-3" />
            <span>All fields below are locked. Only the payment slip{canEditNotes ? " and admin notes" : ""} can still be updated.</span>
          </div>
        )}
        <Field label="Treatment partner">{d.destination_name}</Field>
        <Field label="Type">{d.destination_type}</Field>
        <Field label="Material">
          <span className="px-2 py-0.5 rounded-md bg-[#D2E4D6] text-[#056839] text-[10px] font-semibold uppercase tracking-wider">
            {d.material_type}
          </span>
        </Field>
        <Field label="Weight">
          <span className="text-[#056839] font-semibold">{Number(d.weight_kg || 0).toFixed(1)} kg</span>
        </Field>
        <Field label="Transaction date">{fmt(d.transaction_at)}</Field>
        <Field label="Source output batches">{(shipment?.source_output_ids || []).length}</Field>
        {d.invoice_id && (
          <Field label="Invoice ID" testId="detail-shipment-invoice-id">
            <span className="font-mono text-[#3F3F3F]">{d.invoice_id}</span>
          </Field>
        )}
        {shipment?.reviewed_by_email && (
          <Field label="Reviewed by" testId="detail-reviewed-by">
            {shipment.reviewed_by_name
              ? <>{shipment.reviewed_by_name} <span className="text-[#3F3F3F]/60">({shipment.reviewed_by_email})</span></>
              : shipment.reviewed_by_email}
          </Field>
        )}
        {shipment?.reviewed_at && (
          <Field label="Review date" testId="detail-reviewed-at">{fmt(shipment.reviewed_at)}</Field>
        )}
      </div>

      {/* Provenance — traces back to processing batches and originating
          collections. Read-only chips that open the existing detail dialogs. */}
      <div className="mt-5" data-testid="detail-shipment-provenance">
        <div className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">Provenance</div>
        {prov === null && !provErr && (
          <div className="mt-2 text-sm text-[#3F3F3F]/60 flex items-center gap-2" data-testid="provenance-loading">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Tracing back to source…
          </div>
        )}
        {provErr && (
          <div className="mt-2 text-sm text-[#A0522D]" data-testid="provenance-error">{provErr}</div>
        )}
        {prov && prov.traceable === false && (
          <div
            className="mt-2 rounded-lg border border-dashed border-[#E4D5B7] bg-[#FBF3EA] p-3 text-sm text-[#A0522D]"
            data-testid="provenance-not-traceable"
          >
            Not traceable — no linked collection/processing
          </div>
        )}
        {prov && prov.traceable && (
          <div className="mt-2 space-y-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#3F3F3F]/50 mb-1">
                Processing Batch{(prov.processing_ids?.length || 0) === 1 ? "" : "es"} ({prov.processing_ids?.length || 0})
              </div>
              <div className="flex flex-wrap gap-2">
                {(prov.processing_ids || []).map((pid) => (
                  <button
                    key={pid}
                    type="button"
                    onClick={() => openChip("processed", pid)}
                    disabled={!!loadingChip}
                    className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full border border-[#056839] text-[#056839] bg-white text-xs font-mono hover:bg-[#056839] hover:text-white transition-colors disabled:opacity-50"
                    data-testid={`provenance-processing-${pid}`}
                  >
                    {loadingChip?.kind === "processed" && loadingChip?.id === pid ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Factory className="w-3 h-3" />
                    )}
                    {String(pid).slice(0, 8)}…
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#3F3F3F]/50 mb-1">
                Collection{(prov.input_collection_ids?.length || 0) === 1 ? "" : "s"} ({prov.input_collection_ids?.length || 0})
              </div>
              <div className="flex flex-wrap gap-2">
                {(prov.input_collection_ids || []).map((cid) => (
                  <button
                    key={cid}
                    type="button"
                    onClick={() => openChip("received", cid)}
                    disabled={!!loadingChip}
                    className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full border border-[#056839] text-[#056839] bg-white text-xs font-mono hover:bg-[#056839] hover:text-white transition-colors disabled:opacity-50"
                    data-testid={`provenance-collection-${cid}`}
                  >
                    {loadingChip?.kind === "received" && loadingChip?.id === cid ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Package className="w-3 h-3" />
                    )}
                    {String(cid).slice(0, 8)}…
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {d.additional_info && !canEditNotes && (
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">
            Additional information
          </div>
          <div
            className="mt-1 rounded-lg border border-[#D2E4D6] bg-[#F9F8F6] p-3 text-sm text-[#3F3F3F] whitespace-pre-wrap"
            data-testid="detail-additional-info"
          >
            {liveDest.additional_info || d.additional_info}
          </div>
        </div>
      )}

      {/* Post-approval records-only update. Approved shipments are locked from
          re-review, but the payment slip (both roles) and notes (admin only)
          may still be attached / corrected after the fact. Everything else on
          the record stays visually locked. */}
      {canEditPayment && (
        <PostApprovalEditor
          shipment={shipment}
          destination={liveDest}
          canEditNotes={canEditNotes}
          role={role}
          fileUrl={fileUrl}
          onSaved={(updatedFields) => {
            setLiveDest((prev) => ({ ...prev, ...updatedFields }));
          }}
        />
      )}

      <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="detail-shipment-evidence">
        <EvidenceCard
          label="Sales invoice"
          paths={d.invoice_path ? [d.invoice_path] : []}
          fileUrl={fileUrl}
          testId="detail-shipment-invoice"
          locked={isApproved}
        />
        <EvidenceCard
          label="Material photo"
          paths={collectPaths(d.material_photos, d.material_photo_path)}
          fileUrl={fileUrl}
          testId="detail-shipment-material"
          locked={isApproved}
        />
        <EvidenceCard
          label="Bank transfer proof"
          paths={collectPaths(liveDest.payment_proofs, liveDest.payment_proof_path)}
          fileUrl={fileUrl}
          testId="detail-shipment-payment"
          locked={isApproved && !canEditPayment}
          hidden={canEditPayment}
        />
      </div>
    </>
  );
}

function collectPaths(arr, singleton) {
  // Prefer multi-file arrays (objects with .path or raw strings); fall back
  // to legacy single-string field. Always returns an array of string paths.
  const out = [];
  if (Array.isArray(arr)) {
    for (const item of arr) {
      const p = (item && typeof item === "object") ? (item.path || item.storage_path || item.url) : item;
      if (p) out.push(p);
    }
  }
  if (out.length === 0 && singleton) out.push(singleton);
  return out;
}

function EvidenceCard({ label, paths = [], fileUrl, testId, locked = false, hidden = false }) {
  if (hidden) return null;
  return (
    <div className="rounded-xl border border-[#D2E4D6]/80 p-4 bg-[#F9F8F6] relative" data-testid={testId}>
      {locked && (
        <span
          className="absolute top-2 right-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-[#3F3F3F]/8 text-[#3F3F3F]/60 text-[9px] font-semibold uppercase tracking-wider"
          title="Locked after approval"
          data-testid={`${testId}-locked`}
        >
          <Lock className="w-2.5 h-2.5" /> Locked
        </span>
      )}
      <div className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">
        {label}
        {paths.length > 1 && (
          <span className="ml-1 normal-case tracking-normal text-[#3F3F3F]/60">({paths.length})</span>
        )}
      </div>
      {paths.length === 0 ? (
        <div className="mt-2 text-sm text-[#3F3F3F]/60">Not attached</div>
      ) : (
        <div className="mt-2 space-y-2">
          {paths.map((p, i) => {
            const url = fileUrl(p);
            const isImage = /\.(jpe?g|png|webp|gif|heic|heif)$/i.test(p);
            return (
              <div key={p || `slip-${i}`} className="flex items-start gap-2">
                {isImage && (
                  <a
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    className="block w-12 h-12 rounded-md overflow-hidden border border-[#D2E4D6] shrink-0"
                    data-testid={`${testId}-thumb-${i}`}
                  >
                    <img
                      src={url}
                      alt=""
                      className="w-full h-full object-cover"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  </a>
                )}
                <div className="flex flex-wrap gap-2 flex-1">
                  <a
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-[#056839] text-white text-sm font-medium hover:bg-[#044E2A]"
                    data-testid={`${testId}-view-${i}`}
                  >
                    View
                  </a>
                  <a
                    href={url}
                    download
                    className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg border border-[#056839] text-[#056839] text-sm font-medium hover:bg-[#F0FAF4]"
                    data-testid={`${testId}-download-${i}`}
                  >
                    Download
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PostApprovalEditor({ shipment, destination, canEditNotes, role, fileUrl, onSaved }) {
  // Local editing state; seeded from destination on mount/change so a fresh
  // dialog always starts from server truth.
  const [slips, setSlips] = useState(() =>
    (destination.payment_proofs || [])
      .filter((p) => p && (p.path || p.storage_path))
      .map((p) => ({
        path: p.path || p.storage_path,
        date: p.date || "",
        filename: p.filename || ((p.path || p.storage_path || "").split("/").pop() || "slip"),
        uploading: false,
      }))
  );
  const [notes, setNotes] = useState(destination.additional_info || "");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    setSlips(
      (destination.payment_proofs || [])
        .filter((p) => p && (p.path || p.storage_path))
        .map((p) => ({
          path: p.path || p.storage_path,
          date: p.date || "",
          filename: p.filename || ((p.path || p.storage_path || "").split("/").pop() || "slip"),
          uploading: false,
        }))
    );
    setNotes(destination.additional_info || "");
    setDirty(false);
  }, [destination.id, destination.payment_proofs, destination.additional_info]);

  async function uploadFile(file) {
    if (!file) return;
    // Add a placeholder row while the upload runs
    const localId = `local-${Date.now()}`;
    setSlips((prev) => [...prev, { path: null, date: "", filename: file.name, uploading: true, _localId: localId }]);
    setDirty(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/uploads/document", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const path = res.data?.storage_path || res.data?.path || "";
      setSlips((prev) => prev.map((s) => (s._localId === localId ? { path, date: s.date, filename: s.filename, uploading: false } : s)));
    } catch (e) {
      setSlips((prev) => prev.filter((s) => s._localId !== localId));
      toast.error(e?.response?.data?.detail || "Slip upload failed");
    }
  }

  function removeSlip(idx) {
    setSlips((prev) => prev.filter((_, i) => i !== idx));
    setDirty(true);
  }

  function updateSlipDate(idx, date) {
    setSlips((prev) => prev.map((s, i) => (i === idx ? { ...s, date } : s)));
    setDirty(true);
  }

  async function save() {
    if (slips.some((s) => s.uploading)) {
      toast.error("Wait for uploads to finish");
      return;
    }
    setSaving(true);
    try {
      const body = {
        payment_proofs: slips
          .filter((s) => s.path)
          .map((s) => ({ path: s.path, date: s.date || null })),
      };
      if (canEditNotes) {
        body.additional_info = notes;
      }
      const { data } = await api.patch(
        `/shipments/${shipment.id}/destinations/${destination.id}/post-approval`,
        body
      );
      toast.success(
        role === "facility"
          ? "Payment slip updated. Admin has been notified."
          : "Approved shipment updated."
      );
      onSaved?.({
        payment_proofs: body.payment_proofs,
        ...(canEditNotes ? { additional_info: notes || null } : {}),
      });
      setDirty(false);
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="mt-5 rounded-xl border border-[#D2E4D6] bg-[#F0FAF4] p-4"
      data-testid="post-approval-editor"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#056839] font-semibold">
            Records-only update
          </div>
          <p className="mt-1 text-xs text-[#3F3F3F]/80 leading-relaxed">
            This shipment is approved and locked from review.{" "}
            {role === "facility"
              ? "You may still attach or replace the payment slip — the admin will be notified of every change."
              : "You may still update the payment slip or add notes."}
          </p>
          {shipment?.post_approval_updated_at && (
            <p className="mt-1 text-[11px] text-[#3F3F3F]/60" data-testid="post-approval-audit">
              Last records-only update: <strong>{formatDateTime(shipment.post_approval_updated_at)}</strong>
              {shipment.post_approval_updated_by_email ? ` by ${shipment.post_approval_updated_by_email}` : ""}
              {shipment.post_approval_updated_by_role ? ` (${shipment.post_approval_updated_by_role})` : ""}
            </p>
          )}
        </div>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium mb-2">
          Bank transfer proof{slips.length > 1 ? ` (${slips.length})` : ""}
        </div>
        <div className="space-y-2">
          {slips.map((s, i) => (
            <div
              key={s.path || s._localId || `slip-${i}`}
              className="flex flex-wrap items-center gap-2 rounded-lg border border-[#D2E4D6] bg-white px-3 py-2"
              data-testid={`post-approval-slip-${i}`}
            >
              {s.uploading ? (
                <Loader2 className="w-4 h-4 animate-spin text-[#69A488]" />
              ) : (
                <FileCheck className="w-4 h-4 text-[#056839]" />
              )}
              <span className="flex-1 truncate text-sm text-[#3F3F3F]">{s.filename || "slip"}</span>
              <Input
                type="date"
                value={s.date || ""}
                onChange={(e) => updateSlipDate(i, e.target.value)}
                className="h-8 w-36 text-xs"
                data-testid={`post-approval-slip-date-${i}`}
              />
              {s.path && (
                <a
                  href={fileUrl(s.path)}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 px-2 h-8 rounded-md bg-[#056839] text-white text-xs font-medium hover:bg-[#044E2A]"
                  data-testid={`post-approval-slip-view-${i}`}
                >
                  View
                </a>
              )}
              <button
                type="button"
                onClick={() => removeSlip(i)}
                disabled={s.uploading}
                className="inline-flex items-center justify-center w-7 h-7 rounded-md text-[#A0522D] hover:bg-[#FBF3EA] disabled:opacity-40"
                data-testid={`post-approval-slip-remove-${i}`}
                aria-label="Remove slip"
              >
                <XIcon className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf,image/jpeg,image/png,image/webp"
            className="hidden"
            data-testid="post-approval-slip-input"
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (f) uploadFile(f);
            }}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="w-full inline-flex items-center justify-center gap-2 text-sm px-3 h-10 rounded-lg border-2 border-dashed border-[#69A488] bg-white hover:bg-[#F0FAF4] text-[#056839] font-medium"
            data-testid="post-approval-slip-add"
          >
            <Upload className="w-4 h-4" />
            {slips.length === 0 ? "Attach payment slip (PDF / image)" : "Add another slip"}
          </button>
        </div>
      </div>

      {canEditNotes && (
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium mb-1">
            Additional information{" "}
            <span className="normal-case tracking-normal text-[#3F3F3F]/50">admin-editable</span>
          </div>
          <Textarea
            value={notes}
            onChange={(e) => {
              setNotes(e.target.value);
              setDirty(true);
            }}
            rows={3}
            className="resize-none"
            placeholder="Notes visible on the shipment record…"
            maxLength={2000}
            data-testid="post-approval-notes"
          />
        </div>
      )}

      <div className="mt-4 flex justify-end">
        <Button
          onClick={save}
          disabled={saving || !dirty || slips.some((s) => s.uploading)}
          className="bg-[#056839] hover:bg-[#044E2A] text-white h-10 px-5"
          data-testid="post-approval-save"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Save changes</>}
        </Button>
      </div>
    </div>
  );
}

